/**
 * @lodex-pro/web-portal/hooks/useDisposalBiPanels — Phase 16 / Task 16.6 (DEC-163).
 *
 * TanStack Query hooks for the 3 disposal-portal BI panel endpoints.
 * All three read paths use useApiQuery; the data-export write path uses
 * useApiMutation. Anonymization runs server-side — hooks receive already-
 * anonymized letter-labeled series.
 */

import { useApiOpts, useApiQuery, useApiMutation } from '@lodex-pro/web-shared';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import type {
  BiVolumeTrendsResponse,
  BiAcceptanceBySiteResponse,
  BiDataExportResponse,
  BiDateRange,
  DataExportRequest,
} from '@lodex-pro/shared/validation/disposal-bi-panels';

export function useDisposalBiVolumeTrends(
  range: BiDateRange,
): UseQueryResult<BiVolumeTrendsResponse> {
  const opts = useApiOpts();
  const path = `/v1/disposal-portal/bi/volume-trends?range=${range}`;
  return useApiQuery<BiVolumeTrendsResponse>(opts, path, {
    queryKey: ['disposal-bi-volume-trends', range],
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useDisposalBiAcceptanceBySite(
  range: BiDateRange,
): UseQueryResult<BiAcceptanceBySiteResponse> {
  const opts = useApiOpts();
  const path = `/v1/disposal-portal/bi/acceptance-by-site?range=${range}`;
  return useApiQuery<BiAcceptanceBySiteResponse>(opts, path, {
    queryKey: ['disposal-bi-acceptance-by-site', range],
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useDisposalBiDataExport(): UseMutationResult<
  BiDataExportResponse,
  Error,
  DataExportRequest
> {
  const opts = useApiOpts();
  return useApiMutation<DataExportRequest, BiDataExportResponse>(
    opts,
    '/v1/disposal-portal/bi/data-export',
    'POST',
  );
}
